<template>
  <!-- Top Navigation - Enhanced Header -->
  <nav
    class="ui-nav transition-all duration-700 ease-in-out"
    :class="
      isNavigationMinimized
        ? 'flex p-4 absolute top-4 left-4 z-40'
        : 'block static w-full'
    "
  >
    <div
      class="nav-container transition-all duration-700 ease-in-out"
      :class="
        isNavigationMinimized
          ? (isDayMode
              ? 'flex justify-between items-center bg-stone-900/40 backdrop-blur-md px-6 py-3 mx-auto border border-stone-600/30 shadow-md text-amber-100'
              : 'flex justify-between items-center bg-black/40 backdrop-blur-sm px-6 py-3 mx-auto border border-white/10 text-white')
          : `w-full ${colors.header3DBackground} px-8 py-3`
      "
      :style="{
        borderRadius: isNavigationMinimized ? '50px' : '0',
        transition: 'all 0.7s cubic-bezier(0.4, 0, 0.2, 1)',
        width: isNavigationMinimized ? 'auto' : '100%',
        maxWidth: isNavigationMinimized ? 'none' : '100%',
      }"
    >
      <!-- Full Width Header Layout -->
      <div
        v-if="!isNavigationMinimized"
        class="flex justify-between items-center w-full"
      >
        <!-- Left Section: Logo + Title -->
        <div class="flex items-center space-x-3">
          <div
            @click="toggleNavigation"
            class="ui-button w-9 h-9 rounded-xl border flex items-center justify-center hover:scale-105 transition-all duration-300 shadow-md"
            :class="colors.header3DLogoButton"
            style="pointer-events: auto; cursor: pointer"
          >
            <HugeiconsIcon :icon="AiMagicIcon" :size="20" />
          </div>
          <div class="flex flex-col">
            <h1
              class="ui-title text-xl font-bold transition-all duration-300 tracking-tight"
            >
              <span
                class="transition-colors duration-300"
                :class="colors.header3DTitle"
              >
                {{ strings.siteTitle }}
              </span>
            </h1>
            <p
              class="text-xs font-medium tracking-wider uppercase transition-colors duration-300"
              :class="colors.header3DSubtitle"
            >
              {{ strings.siteSubtitle }}
            </p>
          </div>
        </div>

        <!-- Right Section: Controls -->
        <div class="flex items-center space-x-3">
          <!-- Credits Button -->
          <button
            @click="$emit('openCredits')"
            class="ui-button relative px-3.5 py-2.5 rounded-xl transition-all duration-300 overflow-hidden group shadow-md hover:shadow-lg border"
            :class="colors.header3DButtonBg"
            style="pointer-events: auto; cursor: pointer"
            title="View 3D Model Credits"
          >
            <!-- Content -->
            <div class="relative z-10 flex items-center gap-2">
              <HugeiconsIcon :icon="PaintBoardIcon" :size="18" :class="colors.accentColor" class="transition-transform group-hover:scale-110" />
              <span v-if="!isMobile" class="font-medium text-sm">
                Credits
              </span>
            </div>
          </button>

          <!-- Classic Navigation Button -->
          <router-link
            to="/about"
            class="ui-button relative px-3.5 py-2.5 rounded-xl transition-all duration-300 overflow-hidden group shadow-md hover:shadow-lg border"
            :class="colors.header3DButtonBg"
            style="pointer-events: auto; cursor: pointer"
            title="Switch to Classic Navigation"
          >
            <!-- Content -->
            <div class="relative z-10 flex items-center gap-2">
              <HugeiconsIcon :icon="Book01Icon" :size="18" :class="colors.accentColor" class="transition-transform group-hover:scale-110" />
              <span v-if="!isMobile" class="font-medium text-sm">
                Classic View
              </span>
            </div>
          </router-link>

          <!-- Day/Night Mode Toggle -->
          <button
            @click="toggleDayNightMode"
            class="ui-button day-night-toggle relative p-2.5 rounded-xl transition-all duration-300 overflow-hidden group shadow-md hover:shadow-lg border"
            :class="colors.header3DButtonBg"
            style="pointer-events: auto; cursor: pointer"
            :title="isDayMode ? 'Switch to Night Mode' : 'Switch to Day Mode'"
          >
            <!-- Icon container -->
            <div class="relative z-10 flex items-center justify-center">
              <!-- Day icon (sun) -->
              <div
                v-if="isDayMode"
                class="w-5 h-5 text-amber-300 transition-all duration-300 transform group-hover:scale-110 group-hover:rotate-12"
              >
                <svg fill="currentColor" viewBox="0 0 24 24">
                  <path
                    d="M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.758 17.303a.75.75 0 00-1.061-1.06l-1.591 1.59a.75.75 0 001.06 1.061l1.591-1.59zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.697 7.757a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 00-1.061 1.06l1.59 1.591z"
                  />
                </svg>
              </div>

              <!-- Night icon (moon) -->
              <div
                v-else
                class="w-5 h-5 transition-all duration-300 transform group-hover:scale-110 group-hover:-rotate-12"
                :class="colors.accentColor"
              >
                <svg fill="currentColor" viewBox="0 0 24 24">
                  <path
                    fill-rule="evenodd"
                    d="M9.528 1.718a.75.75 0 01.162.819A8.97 8.97 0 009 6a9 9 0 009 9 8.97 8.97 0 003.463-.69.75.75 0 01.981.98 10.503 10.503 0 01-9.694 6.46c-5.799 0-10.5-4.701-10.5-10.5 0-4.368 2.667-8.112 6.46-9.694a.75.75 0 01.818.162z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>
            </div>
          </button>
        </div>
      </div>

      <!-- Minimized Layout (existing) -->
      <div v-else class="flex justify-between items-center w-full">
        <!-- Logo/Title Section -->
        <div class="flex items-center space-x-4">
          <div
            class="flex items-center space-x-3"
            style="pointer-events: auto; cursor: pointer"
          >
            <div
              @click="toggleNavigation"
              class="ui-button w-8 h-8 rounded-lg border flex items-center justify-center transition-all duration-300"
              :class="colors.header3DLogoButton"
            >
              <HugeiconsIcon :icon="AiMagicIcon" :size="18" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { defineProps, ref, onMounted, onUnmounted } from "vue";
import { isMobileDevice } from "@/utils/deviceDetection"; // adjust path if needed
import { useTheme } from "@/stores/themeStore";
import { HugeiconsIcon } from "@hugeicons/vue";
import {
  AiMagicIcon,
  PaintBoardIcon,
  Book01Icon,
} from "@hugeicons/core-free-icons";

const { strings, colors } = useTheme();

const isMobile = ref(false);
function handleResize() {
  isMobile.value = isMobileDevice();
}

onMounted(() => {
  window.addEventListener("resize", handleResize);
  handleResize();
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
});

defineProps<{
  isNavigationMinimized: boolean;
  toggleNavigation: () => void;
  isDayMode: boolean;
  toggleDayNightMode: () => void;
}>();

defineEmits<{
  openCredits: [];
}>();
</script>
